import { useEffect, useRef, useState, useCallback } from 'react';
import { AnamService } from '../../services/anamService';
import { useQuizStore, usePatientSurgeryStore } from '../../hooks';
import { ErrorMessage, LoadingData } from '../../ui';
import './AnamAvatar.css';

export const AnamAvatarQuiz = () => {
  const {
    sessionId,
    initializeAnam,
    initializeChatBot,
    processAnamAnswer,
    sendQuizAnswers,
    setTermsAccepted,
    isAllquestionsAnswered,
    calculateUnansweredQuestions,
  } = useQuizStore();

  const { patientSurgery } = usePatientSurgeryStore();
  const { assessmentQuestionGroups } = patientSurgery?.data?.procedure?.assessment || {};

  const [anamSessionToken, setAnamSessionToken] = useState('');
  const [isInitializing, setIsInitializing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const anamServiceRef = useRef(null);
  const videoElementRef = useRef(null);
  const questionnaireFlowRef = useRef(false);

  // Get all questions in order
  const allQuestions = assessmentQuestionGroups?.flatMap(
    (group) => group.questionGroup?.questions || []
  ) || [];

  // Function to close ANAM session and clean up
  const closeAnamSession = useCallback(async () => {
    try {
      console.log('[ANAM QUIZ] Closing ANAM session...');
      
      // Disconnect the service
      if (anamServiceRef.current) {
        await anamServiceRef.current.disconnect();
        anamServiceRef.current = null;
      }
      
      // Remove token from localStorage
      localStorage.removeItem('anam_session_token');
      console.log('[ANAM QUIZ] Session token removed from localStorage');
      
      // Clear state
      setAnamSessionToken('');
    } catch (error) {
      console.error('[ANAM QUIZ] Error closing ANAM session:', error);
      // Still try to clean up localStorage even if disconnect fails
      localStorage.removeItem('anam_session_token');
    }
  }, []);

  const initializeAnamService = useCallback(async () => {
    if (anamServiceRef.current || !videoElementRef.current || !anamSessionToken) {
      return;
    }

    try {
      setIsInitializing(true);
      setError('');

      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Wait for video element to be ready
      let retries = 0;
      const maxRetries = 20;
      while (!videoElementRef.current && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }

      if (!videoElementRef.current) {
        throw new Error('Video element not available');
      }

      const anamService = new AnamService({
        onReady: () => {
          console.log('[ANAM QUIZ] Service ready, starting questionnaire flow...');
          setIsReady(true);
          setIsInitializing(false);
          startQuestionnaireFlow();
        },
        onError: (err) => {
          console.error('[ANAM QUIZ] Service error:', err);
          setError(err.message || 'Error al inicializar avatar');
          setIsInitializing(false);
        },
        onSpeechStart: () => {
          // Microphone will be stopped automatically by AnamService
        },
        onSpeechEnd: () => {
          // Ready for next question
        },
      });

      anamServiceRef.current = anamService;
      await anamService.initialize(anamSessionToken, videoElementRef.current);
    } catch (err) {
      console.error('[ANAM QUIZ] Failed to initialize:', err);
      const errorMessage = err?.message || 'Error al inicializar avatar';
      setError(errorMessage);
      setIsInitializing(false);
    }
  }, [anamSessionToken]);

  const startQuestionnaireFlow = useCallback(async () => {
    if (questionnaireFlowRef.current || !anamServiceRef.current) {
      return;
    }

    questionnaireFlowRef.current = true;

    try {
      // Initialize LangChain conversation first
      if (!sessionId) {
        // Need to initialize conversation - but we need to do this through the chatbot initialization
        // For now, let's use the sessionId if available, otherwise we'll need to handle it
        setError('Necesitamos inicializar la conversación primero');
        questionnaireFlowRef.current = false;
        return;
      }

      // Start with first unanswered question
      const unanswered = calculateUnansweredQuestions();
      
      // If no unanswered questions, we're done
      if (unanswered.length === 0) {
        // Ask for consent and submit
        if (anamServiceRef.current) {
          try {
            const stopMicrophone = () => {};
            await anamServiceRef.current.sendMessage(
              '¡Encuesta completada! Gracias por tus respuestas.',
              stopMicrophone
            );
            
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            const consentAnswer = await anamServiceRef.current.askQuestion(
              'Ahora necesito tu consentimiento para el tratamiento de datos. ¿Aceptas? (Responde "Sí" para continuar)',
              stopMicrophone
            );

                if (consentAnswer && (consentAnswer.toLowerCase().includes('sí') || consentAnswer.toLowerCase().includes('si'))) {
                  setTermsAccepted(true);
                  await sendQuizAnswers();
                  
                  // Close ANAM session after quiz is successfully submitted
                  await closeAnamSession();
                }
          } catch (err) {
            console.error('[ANAM QUIZ] Error with completion:', err);
          }
        }
        questionnaireFlowRef.current = false;
        return;
      }

      // Process unanswered questions
      for (const question of unanswered) {
        if (!anamServiceRef.current) break;

        const questionIndex = allQuestions.findIndex(q => q.id === question.id);
        setCurrentQuestionIndex(questionIndex >= 0 ? questionIndex : 0);

        const stopMicrophone = () => {};

        try {
          // Ask question via ANAM
          const answer = await anamServiceRef.current.askQuestion(question.title, stopMicrophone);
          
          if (answer && answer.trim()) {
            // Process answer through LangChain API
            setIsProcessing(true);
            await processAnamAnswer(answer);
            setIsProcessing(false);
          }

          // Check if we're done
          const remainingUnanswered = calculateUnansweredQuestions();
          if (remainingUnanswered.length === 0) {
            // All questions answered - ask for consent
            if (anamServiceRef.current) {
              try {
                await anamServiceRef.current.sendMessage(
                  '¡Encuesta completada! Gracias por tus respuestas.',
                  stopMicrophone
                );
                
                await new Promise(resolve => setTimeout(resolve, 2000));
                
                const consentAnswer = await anamServiceRef.current.askQuestion(
                  'Ahora necesito tu consentimiento para el tratamiento de datos. ¿Aceptas? (Responde "Sí" para continuar)',
                  stopMicrophone
                );

                if (consentAnswer && (consentAnswer.toLowerCase().includes('sí') || consentAnswer.toLowerCase().includes('si'))) {
                  setTermsAccepted(true);
                  await sendQuizAnswers();
                  
                  // Close ANAM session after quiz is successfully submitted
                  await closeAnamSession();
                }
              } catch (err) {
                console.error('[ANAM QUIZ] Error with completion:', err);
              }
            }
            break;
          }
        } catch (err) {
          console.error('[ANAM QUIZ] Error asking question:', err);
          setError(err.message || 'Error al procesar pregunta');
          break;
        }
      }

      questionnaireFlowRef.current = false;
    } catch (err) {
      console.error('[ANAM QUIZ] Error in questionnaire flow:', err);
      setError(err.message || 'Error en el flujo de la encuesta');
      questionnaireFlowRef.current = false;
    }
  }, [allQuestions, calculateUnansweredQuestions, processAnamAnswer, sendQuizAnswers, setTermsAccepted, sessionId, closeAnamSession]);

  // Initialize both ANAM session token and LangChain conversation
  useEffect(() => {
    const initializeEverything = async () => {
      // Get Anam session token if we don't have it
      if (!anamSessionToken) {
        // Check localStorage first
        const storedToken = localStorage.getItem('anam_session_token');
        if (storedToken) {
          console.log('[ANAM QUIZ] Using stored session token from localStorage');
          setAnamSessionToken(storedToken);
          return;
        }

        try {
          const token = await initializeAnam();
          if (token) {
            // Store token in localStorage for persistence
            localStorage.setItem('anam_session_token', token);
            console.log('[ANAM QUIZ] Session token stored in localStorage');
            setAnamSessionToken(token);
          }
        } catch (err) {
          console.error('[ANAM QUIZ] Failed to get Anam session token:', err);
          setError(err.message || 'Error al obtener token de sesión ANAM');
          return;
        }
      }
      
      // Initialize LangChain conversation if we don't have sessionId
      if (!sessionId) {
        try {
          await initializeChatBot();
        } catch (err) {
          console.error('[ANAM QUIZ] Failed to initialize LangChain conversation:', err);
          setError(err.message || 'Error al inicializar conversación');
        }
      }
    };

    initializeEverything();
  }, [anamSessionToken, sessionId, initializeAnam, initializeChatBot]);

  // Initialize service when token is ready
  useEffect(() => {
    if (anamSessionToken && !anamServiceRef.current) {
      initializeAnamService();
    }
  }, [anamSessionToken, initializeAnamService]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (anamServiceRef.current) {
        anamServiceRef.current.disconnect().catch(console.error);
      }
      // Clean up localStorage on unmount as well
      localStorage.removeItem('anam_session_token');
    };
  }, []);

  const currentQuestion = allQuestions[currentQuestionIndex] || null;

  return (
    <div className="anam-avatar-quiz-container">
      <div className="text-center mb-3">
        <p className="text-muted mb-0">
          El avatar te hará preguntas. Responde con tu voz.
        </p>
      </div>

      {error && (
        <div className="mb-3">
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      )}

      {(isInitializing || !isReady) && (
        <div className="text-center mb-3">
          <LoadingData />
          <p className="text-muted mt-2">Inicializando avatar...</p>
        </div>
      )}

      <div className="anam-avatar-wrapper mb-4">
        <div className="anam-avatar-container">
          <div className="anam-video-wrapper">
            <video
              ref={videoElementRef}
              autoPlay
              playsInline
              muted={false}
              className="anam-video"
            />
            {!isReady && (
              <div className="anam-loading-overlay">
                <div className="loading-spinner"></div>
                <p>Conectando al avatar...</p>
              </div>
            )}
          </div>
          {isReady && (
            <div className="anam-status-indicator">
              <span className="status-dot"></span>
              <span>Avatar Listo</span>
            </div>
          )}
        </div>
      </div>

      {currentQuestion && isReady && (
        <div className="current-question-card card p-3 mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">
              Pregunta {currentQuestionIndex + 1} de {allQuestions.length}
            </small>
            {isProcessing && (
              <small className="text-primary">
                <LoadingData />
              </small>
            )}
          </div>
          <p className="mb-0 fw-semibold">{currentQuestion.title}</p>
        </div>
      )}

      {isReady && !isProcessing && (
        <div className="listening-indicator text-center">
          <div className="d-inline-flex align-items-center gap-2">
            <span className="badge bg-success">●</span>
            <small className="text-muted">Escuchando tu respuesta...</small>
          </div>
        </div>
      )}
    </div>
  );
};

